import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Container, Row } from 'react-bootstrap';
import DonutChart from '../../../components/ApexCharts/DonutChart';
import Button from '../../../components/Button';
import GenericCard from '../../../components/GenericCards';
import IconButton from '../../../components/IconButton';
import Label from '../../../components/Label';
import useMobile from '../../../hooks/useMobileHook';
import PopoverWithLinks from '../../widgets/PopoverLinks/PopoverLinks';
import {
  addOrRemoveDaysToGivenDate,
  DateFormats,
  formatDate,
} from '../../../utils/utils';

const donutChartData = {
  // The "grid padding - left" setting is a hack to reduce the huge margin to the left that donut renders.
  // See: https://github.com/apexcharts/apexcharts.js/issues/1272
  grid: {
    padding: {
      left: -20,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],
  tooltip: {
    enabled: false,
  },

  // DISPLAY TEXT IN DONUT CENTRE:
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$341,550`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const today = new Date();
export const Investments = () => {
  const investmentMixData = [
    {
      color: '#074CBC',
      title: 'Target Date Funds',
      subtitleLinks: [
        {
          title: 'Vanguard Target Retire 2060 Tr I',
          link: '#',
        },
      ],
      balance: '$105,429.15',
      futureInvestments: '30%',
      ytdRor: '3.12%',
    },
    {
      color: '#F48462',
      title: 'Domestic Equity/Stock',
      subtitleLinks: [
        {
          title: 'Vanguard Dividend Growth Inv',
          link: '#',
        },
        {
          title: 'BlackRock Advantage Small Cap Core Fund',
          link: '#',
        },
      ],
      balance: '$121,709.88',
      futureInvestments: '35%',
      ytdRor: '2.98%',
    },
    {
      color: '#EEA968',
      title: 'International Equity/Stock',
      subtitleLinks: [
        {
          title: 'Dodge & Cox International Stock Fund',
          link: '#',
        },
      ],
      balance: '$64,013.37',
      futureInvestments: '20%',
      ytdRor: '-1.08%',
    },
    {
      color: '#0C9C8E',
      title: 'Bonds/Fixed Income',
      subtitleLinks: [
        {
          title: 'Daily Liquidity Aggregate Bond Index Fund',
          link: '#',
        },
      ],
      balance: '$50,397.60',
      futureInvestments: '15%',
      ytdRor: '1.26%',
    },
  ];

  const isMobile = useMobile();

  const linksData = [
    {
      linkHeader: 'Change Investments',
      route: '#',
    },

    {
      linkHeader: 'Fund Price & Performance',
      route: `#`,
    },
  ];

  return (
    <>
      <GenericCard variant={'white'} className={'mt-9'}>
        <Row className="justify-content-between">
          <Col className={'col-9'}>
            <h5 className={'text-20 pb-0 mb-0'}>Investment Mix</h5>
            <Label className={'lh-xs p-0'}>
              <span className="text-dark-gray-50 p-0">
                {`As of ${formatDate(
                  addOrRemoveDaysToGivenDate(today, -1),
                  DateFormats.SHORT_MONTH_DAY_YEAR
                )}`}
              </span>
            </Label>
          </Col>
          <Col className={'col-2 text-end'}>
            <PopoverWithLinks linksData={linksData} />
          </Col>
        </Row>
        <Row className="justify-content-center pt-8">
          <Col className={'col-8 col-lg-3'}>
            <DonutChart
              series={donutChartData.series}
              options={donutChartData}
            />
          </Col>
          <Col className={'col-12 col-lg-9'}>
            <Container>
              <Row>
                <Col>
                  <Row className="text-fs-6 fw-light pt-3 lh-xs border-bottom">
                    <Col className="text-end fw-normal pe-0 col-10 col-lg-7"></Col>
                    <Col className="text-end fw-normal pe-0 col-1">Balance</Col>
                    <Col className="text-end fw-normal pe-0 col-2 d-none d-lg-block">
                      Future Investments
                    </Col>
                    <Col className="text-end fw-normal pe-0 col-2 d-none d-lg-block">
                      YTD RoR
                    </Col>
                  </Row>

                  {investmentMixData.map((investmentMixDataPoint, index) => {
                    return (
                      <Row
                        className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3 border-bottom "
                        key={index}
                      >
                        <Col className="pt-1 ps-0 col-1 pe-0">
                          <div
                            className="custom-legend-marker"
                            style={{
                              backgroundColor: `${investmentMixDataPoint.color}`,
                            }}
                          ></div>
                        </Col>
                        <Col className="col-9 col-lg-6 ps-0 ">
                          {investmentMixDataPoint.title}
                          <Row>
                            {investmentMixDataPoint.subtitleLinks.map(
                              (link, key) => {
                                return (
                                  <Col className={' col-12'}>
                                    <span
                                      className={'text-blue w-100'}
                                      key={key}
                                    >
                                      {link.title}
                                    </span>
                                  </Col>
                                );
                              }
                            )}
                          </Row>
                        </Col>

                        <Col className="col-1 text-end pe-0">
                          {investmentMixDataPoint.balance}
                        </Col>
                        <Col className="col-2 text-end ps-0 pe-0 d-none d-lg-block">
                          {investmentMixDataPoint.futureInvestments}
                        </Col>
                        <Col className="col-2 text-end pe-0 d-none d-lg-block">
                          {investmentMixDataPoint.ytdRor}
                        </Col>
                      </Row>
                    );
                  })}

                  <Row className="text-fs-6 fw-normal lh-xs pt-3 pb-3 pe-lg-0 pe-3">
                    <Col className="pt-1 ps-3 col-10 col-lg-7">TOTAL</Col>
                    <Col className="col-1 col-lg-1 text-end ps-0  ps-lg-4 pe-0">
                      $341,550
                    </Col>

                    <Col className="col-12 col-lg-4 text-end mt-5 mt-lg-0 pe-0 ps-lg-12 ">
                      <Button className={'w-100 '}>
                        Change Investment Mix
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
      </GenericCard>
    </>
  );
};
